"use client"

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button} from "@heroui/button";
import {Input} from "@heroui/input";
import {Card, CardBody, CardHeader} from "@heroui/card";
import {Select, SelectItem} from "@heroui/react";
import {Divider} from "@heroui/divider";
import {Switch} from "@heroui/switch";
import {EXCHANGE_RATES_TO_PLN, type Currency} from "@/config/exchangeRates";

type Period = "year" | "month" | "daily";
type Rates = Record<Currency, number>;

type Source = {
    id: string;
    title: string;
    amount: string;
    currency: Currency;
    period: Period;
    enabled: boolean;
};

const STORAGE_KEY = "payout-counter-sources";
const ANCHOR_KEY = "payout-counter-anchor";
const RATES_KEY = "payout-counter-rates";
const MAIN_CURRENCY_KEY = "payout-counter-main-currency";

const SECONDS_PER_HOUR = 60 * 60;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;
const SECONDS_PER_MONTH = (365 / 12) * SECONDS_PER_DAY;

const CURRENCIES: Currency[] = ["PLN", "USD", "EUR"];
const PERIODS: Period[] = ["year", "month", "daily"];

const PERIOD_SECONDS: Record<Period, number> = {
    year: 365 * 24 * 60 * 60,
    month: (365 / 12) * 24 * 60 * 60,
    daily: 24 * 60 * 60,
};

const CURRENCY_SYMBOL: Record<Currency, string> = {
    PLN: "zł",
    USD: "$",
    EUR: "€",
};

function makeId() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function emptySource(): Source {
    return {id: makeId(), title: "", amount: "", currency: "PLN", period: "month", enabled: true};
}

function ratePerSecond(source: Source): number {
    const amount = parseFloat(source.amount);
    if (!isFinite(amount)) return 0;
    return amount / PERIOD_SECONDS[source.period];
}

function formatMoney(value: number, currency: Currency): string {
    return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }) + " " + CURRENCY_SYMBOL[currency];
}

function formatCounter(value: number, currency: Currency): string {
    return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
    }) + " " + CURRENCY_SYMBOL[currency];
}

export function App() {
    const [sources, setSources] = useState<Source[] | null>(null);
    const [rates, setRates] = useState<Rates>(EXCHANGE_RATES_TO_PLN);
    const [mainCurrency, setMainCurrency] = useState<Currency>("USD");
    const [anchor, setAnchor] = useState<number | null>(null);
    const [elapsed, setElapsed] = useState(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        let loaded: Source[] = [];
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) loaded = JSON.parse(raw);
        } catch {
            loaded = [];
        }
        if (!Array.isArray(loaded) || loaded.length === 0) {
            loaded = [{id: makeId(), title: "Salary", amount: "3999", currency: "USD", period: "month", enabled: true}];
        }
        loaded = loaded.map(s => ({...s, enabled: s.enabled !== false}));
        setSources(loaded);

        try {
            const rawRates = localStorage.getItem(RATES_KEY);
            if (rawRates) setRates({...EXCHANGE_RATES_TO_PLN, ...JSON.parse(rawRates)});
        } catch {
            /* keep defaults */
        }

        const storedMain = localStorage.getItem(MAIN_CURRENCY_KEY);
        if (storedMain && CURRENCIES.includes(storedMain as Currency)) {
            setMainCurrency(storedMain as Currency);
        }

        const storedAnchor = localStorage.getItem(ANCHOR_KEY);
        const anchorValue = storedAnchor ? +storedAnchor : Date.now();
        if (!storedAnchor) localStorage.setItem(ANCHOR_KEY, String(anchorValue));
        setAnchor(anchorValue);
    }, []);

    useEffect(() => {
        if (sources === null) return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sources));
    }, [sources]);

    useEffect(() => {
        localStorage.setItem(RATES_KEY, JSON.stringify(rates));
    }, [rates]);

    useEffect(() => {
        localStorage.setItem(MAIN_CURRENCY_KEY, mainCurrency);
    }, [mainCurrency]);

    useEffect(() => {
        if (anchor === null) return;
        const tick = () => setElapsed((Date.now() - anchor) / 1000);
        tick();
        intervalRef.current = setInterval(tick, 1000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [anchor]);

    const updateSource = (id: string, patch: Partial<Source>) => {
        setSources(prev => (prev ?? []).map(s => (s.id === id ? {...s, ...patch} : s)));
    };

    const addSource = () => {
        setSources(prev => [...(prev ?? []), emptySource()]);
    };

    const removeSource = (id: string) => {
        setSources(prev => (prev ?? []).filter(s => s.id !== id));
    };

    const resetCounter = () => {
        const now = Date.now();
        localStorage.setItem(ANCHOR_KEY, String(now));
        setAnchor(now);
    };

    const toPLN = (amount: number, currency: Currency) => amount * (rates[currency] || 0);
    const toMain = (amount: number, currency: Currency) =>
        toPLN(amount, currency) / (rates[mainCurrency] || 1);

    const updateRate = (currency: Currency, value: string) => {
        const parsed = parseFloat(value);
        const factor = isFinite(parsed) ? parsed : 0;
        setRates(prev => ({...prev, [currency]: factor * (prev[mainCurrency] || 1)}));
    };

    const totalMain = useMemo(() => {
        let total = 0;
        for (const source of sources ?? []) {
            if (!source.enabled) continue;
            const earned = ratePerSecond(source) * elapsed;
            if (earned <= 0) continue;
            total += toMain(earned, source.currency);
        }
        return total;
    }, [sources, elapsed, rates, mainCurrency]);

    const totalRatesMain = useMemo(() => {
        const sum = (secs: number) =>
            (sources ?? [])
                .filter(s => s.enabled)
                .reduce((acc, s) => acc + toMain(ratePerSecond(s) * secs, s.currency), 0);
        return {
            hour: sum(SECONDS_PER_HOUR),
            day: sum(SECONDS_PER_DAY),
            month: sum(SECONDS_PER_MONTH),
        };
    }, [sources, rates, mainCurrency]);

    if (sources === null) return null;

    return (
        <div className="flex flex-col gap-8 px-5 py-10 max-w-3xl mx-auto w-full">
            <Card>
                <CardHeader className="flex justify-between items-center gap-3">
                    <span className="text-lg font-semibold">Total earned (live)</span>
                    <div className="flex items-center gap-2">
                        <Select
                            aria-label="Main currency"
                            label="Main currency"
                            selectedKeys={[mainCurrency]}
                            onChange={e => setMainCurrency(e.target.value as Currency)}
                            className="w-36"
                            size="sm"
                            disallowEmptySelection
                        >
                            {CURRENCIES.map(c => (
                                <SelectItem key={c}>{c}</SelectItem>
                            ))}
                        </Select>
                        <Button size="sm" variant="flat" onPress={resetCounter}>Reset time</Button>
                    </div>
                </CardHeader>
                <CardBody className="flex flex-col gap-2">
                    {sources.length === 0 ? (
                        <span className="text-default-400">Add a source to start counting…</span>
                    ) : (
                        <span className="text-4xl md:text-5xl font-mono font-bold tabular-nums">
                            {formatCounter(totalMain, mainCurrency)}
                        </span>
                    )}
                    <span className="text-default-400 text-xs mt-1">
                        ≈ {formatMoney(totalRatesMain.hour, mainCurrency)} / h ·{" "}
                        {formatMoney(totalRatesMain.day, mainCurrency)} / day ·{" "}
                        {formatMoney(totalRatesMain.month, mainCurrency)} / month
                    </span>
                    <span className="text-default-400 text-xs">
                        counting for {Math.floor(elapsed)}s
                    </span>
                </CardBody>
            </Card>

            <div className="flex flex-col gap-4">
                {sources.map(source => {
                    const earned = ratePerSecond(source) * elapsed;
                    return (
                        <Card key={source.id} className={source.enabled ? "" : "opacity-60"}>
                            <CardBody className="flex flex-col gap-4">
                                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                                    <Input
                                        label="Source"
                                        placeholder="e.g. Salary, Pet project"
                                        value={source.title}
                                        onValueChange={v => updateSource(source.id, {title: v})}
                                        className="flex-1"
                                    />
                                    <Input
                                        label="Amount"
                                        type="number"
                                        placeholder="0"
                                        value={source.amount}
                                        onValueChange={v => updateSource(source.id, {amount: v})}
                                        className="md:w-32"
                                    />
                                    <Select
                                        label="Currency"
                                        selectedKeys={[source.currency]}
                                        onChange={e => updateSource(source.id, {currency: e.target.value as Currency})}
                                        className="md:w-28"
                                        disallowEmptySelection
                                    >
                                        {CURRENCIES.map(c => (
                                            <SelectItem key={c}>{c}</SelectItem>
                                        ))}
                                    </Select>
                                    <Select
                                        label="Per"
                                        selectedKeys={[source.period]}
                                        onChange={e => updateSource(source.id, {period: e.target.value as Period})}
                                        className="md:w-32"
                                        disallowEmptySelection
                                    >
                                        {PERIODS.map(p => (
                                            <SelectItem key={p}>{p}</SelectItem>
                                        ))}
                                    </Select>
                                    <Button
                                        isIconOnly
                                        color="danger"
                                        variant="flat"
                                        aria-label="Remove source"
                                        onPress={() => removeSource(source.id)}
                                        className="h-14 w-14 shrink-0"
                                    >
                                        ✕
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch
                                        size="sm"
                                        isSelected={source.enabled}
                                        onValueChange={v => updateSource(source.id, {enabled: v})}
                                    >
                                        <span className="text-default-500 text-sm">
                                            {source.enabled ? "Counted in total" : "Excluded from total"}
                                        </span>
                                    </Switch>
                                </div>
                                <Divider/>
                                <div className="flex justify-between items-baseline">
                                    <div className="flex flex-col">
                                        <span className="text-default-500 text-sm">
                                            {source.title || "Untitled source"}
                                        </span>
                                        <span className="text-default-400 text-xs font-mono tabular-nums">
                                            {formatMoney(ratePerSecond(source) * SECONDS_PER_HOUR, source.currency)} / h ·{" "}
                                            {formatMoney(ratePerSecond(source) * SECONDS_PER_DAY, source.currency)} / day ·{" "}
                                            {formatMoney(ratePerSecond(source) * SECONDS_PER_MONTH, source.currency)} / month
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <span className="text-2xl font-mono font-semibold tabular-nums">
                                            {formatCounter(earned, source.currency)}
                                        </span>
                                        {source.currency !== mainCurrency && (
                                            <span className="text-default-400 text-xs font-mono tabular-nums">
                                                ≈ {formatCounter(toMain(earned, source.currency), mainCurrency)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    );
                })}
            </div>

            <Button color="primary" variant="flat" onPress={addSource}>
                + Add money source
            </Button>

            <Card>
                <CardHeader>
                    <span className="text-lg font-semibold">Exchange rates → {mainCurrency}</span>
                </CardHeader>
                <CardBody className="flex flex-col gap-3">
                    <p className="text-default-500 text-sm">
                        Define how many {mainCurrency} equals 1 unit of each currency.
                    </p>
                    <div className="flex flex-col md:flex-row gap-3">
                        {CURRENCIES.map(currency => {
                            const displayRate = (rates[currency] ?? 0) / (rates[mainCurrency] || 1);
                            return (
                                <Input
                                    key={currency}
                                    type="number"
                                    label={`1 ${currency} =`}
                                    endContent={<span className="text-default-400 text-sm">{mainCurrency}</span>}
                                    value={currency === mainCurrency ? "1" : String(displayRate)}
                                    onValueChange={v => updateRate(currency, v)}
                                    isDisabled={currency === mainCurrency}
                                    className="flex-1"
                                />
                            );
                        })}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}
